import { useCallback, useState } from 'react';

export interface UseDeleteConfirmationReturn<T> {
  confirmOpen: boolean;
  confirmLoading: boolean;
  itemToDelete: T | null;
  requestDelete: (item: T) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
}

export function useDeleteConfirmation<T>(
  onDelete: (item: T) => Promise<void>,
): UseDeleteConfirmationReturn<T> {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  const requestDelete = useCallback((item: T) => {
    setItemToDelete(item);
    setConfirmOpen(true);
  }, []);

  const cancelDelete = useCallback(() => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setItemToDelete(null);
  }, [confirmLoading]);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) {
      cancelDelete();
      return;
    }
    setConfirmLoading(true);
    try {
      await onDelete(itemToDelete);
    } finally {
      setConfirmLoading(false);
      cancelDelete();
    }
  }, [itemToDelete, cancelDelete, onDelete]);

  return {
    confirmOpen,
    confirmLoading,
    itemToDelete,
    requestDelete,
    cancelDelete,
    confirmDelete,
  };
}
